
import { baseApi } from '../../../core/baseApi';
import { createCreateMutation, unwrapApiResponse, type ApiResponse } from '../../../patterns/crud';
import { setUserIdMapping } from '../../../../core/auth/services/userIdentity';
import { apiLogger } from '@/core/utils/logger';
import { extractErrorContext } from '@/shared/utils/error';
import type { UserProfileSession } from '@/core/store/slices/userProfileSlice';
import type { Schema } from '@voley/shared-types';
import {
  isValidImageType,
  getInvalidTypeMessage,
} from '@voley/shared-types/constants';

/**
 * API response type for the current user profile.
 *
 * Intersection adds `completedTours` which the backend schema emits but
 * api-types.ts doesn't include yet. Remove the intersection after the
 * next `npm run api:gen`.
 */
export type UserProfile = Schema<'UserResponse'> & {
  completedTours?: Record<string, { completedAt: string }> | null;
};

// Lazy import to avoid circular dependency with store
const getSetUserSession = async () => {
  const { setUserSession } = await import('../../../../core/store/slices/userProfileSlice');
  return setUserSession;
};

export const userApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    // Special case: /users/me/profile doesn't need an ID since it gets the current user
    getUserProfile: builder.query<UserProfile, void>({
      query: () => '/users/me/profile',
      transformResponse: (response: unknown) => {
        const data = unwrapApiResponse(response as ApiResponse) as UserProfile;
        // Ensure user ID mapping is set when profile loads
        if (data) {
          const authId = data.stytchUserId || data.id;
          const apiId = data.id;
          if (authId && apiId) {
            setUserIdMapping(authId, apiId);
            apiLogger.debug('[getUserProfile] Set user ID mapping', { authId, apiId });
          }
        }
        return data;
      },
      providesTags: ['UserProfile'],
      // Keep cached data longer to avoid unnecessary refetches
      keepUnusedDataFor: 600 // Keep for 10 minutes
    }),
    // Enhanced updateUserProfile with automatic Redux session sync
    updateUserProfile: builder.mutation({
      query: ({ id, data }: { id: string; data: Partial<UserProfile> }) => ({
        url: `/users/${id}`,
        method: 'PUT',
        body: data
      }),
      transformResponse: unwrapApiResponse,
      invalidatesTags: ['UserProfile', 'User'],
      async onQueryStarted({ data }, { dispatch, queryFulfilled }) {
        // Optimistic update for getUserProfile query
        const patchResult = dispatch(
          userApi.util.updateQueryData('getUserProfile', undefined, (draft) => {
            Object.assign(draft as Record<string, unknown>, data as unknown);
          })
        );

        try {
          const { data: updatedUser } = await queryFulfilled;
          // Update Redux session cache immediately after server confirms
          const setUserSession = await getSetUserSession();
          dispatch(setUserSession(updatedUser as Partial<UserProfileSession>));

          // Also update the getUserProfile cache with the server response
          dispatch(
            userApi.util.updateQueryData('getUserProfile', undefined, () => updatedUser)
          );
        } catch {
          // Revert optimistic update on error
          patchResult.undo();
        }
      },
    }),
    signupAgencyOwner: createCreateMutation('/auth/signup-agency-owner', 'UserProfile')(builder),
    getUserInvitation: builder.query<unknown, void>({
      query: () => '/users/invitation',
      transformResponse: unwrapApiResponse,
      providesTags: ['User']
    }),
    // Enhanced profile image upload with automatic Redux session sync
    uploadProfileImage: builder.mutation<
      { profileImageUrl: string; thumbnailUrl: string; originalUrl: string },
      { userId: string; file: File; cropData?: string }
    >({
      query: ({ userId, file, cropData }) => {
        // Validate file before creating FormData
        if (!file || file.size === 0) {
          apiLogger.error('[uploadProfileImage] Invalid file - empty or missing', {
            userId,
            hasFile: !!file,
            fileSize: file?.size
          });
          throw new Error('Image file is empty or invalid. Please try selecting the image again.');
        }

        // Validate file type using shared constants
        if (!isValidImageType(file.type)) {
          apiLogger.error('[uploadProfileImage] Invalid file type', {
            userId,
            fileType: file.type
          });
          throw new Error(getInvalidTypeMessage());
        }

        const formData = new FormData();
        formData.append('image', file);
        if (cropData) {
          formData.append('cropData', cropData);
        }

        // Debug logging for profile image upload
        apiLogger.info('[uploadProfileImage] Preparing upload request', {
          userId,
          fileName: file.name,
          fileSize: file.size,
          fileType: file.type,
          hasCropData: !!cropData
        });

        return {
          url: `/users/${userId}/profile-image`,
          method: 'POST',
          body: formData,
        };
      },
      transformResponse: (response: unknown) => {
        apiLogger.info('[uploadProfileImage] Upload successful', response);
        return unwrapApiResponse(response as ApiResponse) as { profileImageUrl: string; thumbnailUrl: string; originalUrl: string };
      },
      invalidatesTags: ['UserProfile', 'User'],
      async onQueryStarted(args, { dispatch, queryFulfilled, getState }) {
        apiLogger.info('[uploadProfileImage] Starting upload for user', { userId: args.userId });
        try {
          const { data: imageResult } = await queryFulfilled;
          apiLogger.info('[uploadProfileImage] Query fulfilled successfully', imageResult);

          // Update RTK Query getUserProfile cache immediately so components re-render
          dispatch(
            userApi.util.updateQueryData('getUserProfile', undefined, (draft) => {
              if (draft) {
                (draft as Record<string, unknown>).profileImageUrl = imageResult.profileImageUrl;
              }
            })
          );

          // Also update Redux session cache for other consumers
          const state = getState() as Record<string, unknown>;
          const currentSession = (state.userProfile as { session?: UserProfileSession | undefined }).session;

          if (currentSession) {
            const updatedSession: Partial<UserProfileSession> = {
              ...currentSession,
              profileImageUrl: imageResult.profileImageUrl,
            };
            const setUserSession = await getSetUserSession();
            dispatch(setUserSession(updatedSession));
          }
        } catch (error: unknown) {
          // Log detailed error information
          const ctx = extractErrorContext(error);
          apiLogger.error('[uploadProfileImage] Upload failed', {
            userId: args.userId,
            errorStatus: ctx.status,
            errorMessage: ctx.message,
            errorCode: ctx.code,
          });
        }
      },
    }),
    // Record tour completion (fire-and-forget, idempotent)
    completeTour: builder.mutation<{ tourId: string }, string>({
      query: (tourId) => ({
        url: `/users/me/tours/${tourId}/complete`,
        method: 'POST',
      }),
      transformResponse: unwrapApiResponse,
      invalidatesTags: ['UserProfile'],
      async onQueryStarted(tourId, { dispatch, queryFulfilled }) {
        // Optimistic update — mark tour as completed in cache immediately
        const patchResult = dispatch(
          userApi.util.updateQueryData('getUserProfile', undefined, (draft) => {
            if (draft) {
              const draftRecord = draft as Record<string, unknown>;
              const existing = (draftRecord.completedTours ?? {}) as Record<string, { completedAt: string }>;
              draftRecord.completedTours = {
                ...existing,
                [tourId]: { completedAt: new Date().toISOString() },
              };
            }
          })
        );
        try {
          await queryFulfilled;
        } catch {
          patchResult.undo();
        }
      },
    }),
    // Enhanced delete profile image with automatic Redux session sync
    deleteProfileImage: builder.mutation<void, string>({
      query: (userId) => ({
        url: `/users/${userId}/profile-image`,
        method: 'DELETE',
      }),
      transformResponse: unwrapApiResponse,
      invalidatesTags: ['UserProfile', 'User'],
      async onQueryStarted(_userId, { dispatch, queryFulfilled, getState }) {
        try {
          await queryFulfilled;

          // Update RTK Query getUserProfile cache immediately
          dispatch(
            userApi.util.updateQueryData('getUserProfile', undefined, (draft) => {
              if (draft) {
                (draft as Record<string, unknown>).profileImageUrl = null;
              }
            })
          );

          // Also update Redux session cache
          const state = getState() as Record<string, unknown>;
          const currentSession = (state.userProfile as { session?: UserProfileSession | undefined }).session;

          if (currentSession) {
            const updatedSession: Partial<UserProfileSession> = {
              ...currentSession,
              profileImageUrl: null,
            };
            const setUserSession = await getSetUserSession();
            dispatch(setUserSession(updatedSession));
          }
        } catch {
          // Error handled by component
        }
      },
    }),
  }),
  overrideExisting: true,
});

export const {
  useGetUserProfileQuery,
  useLazyGetUserProfileQuery,
  useUpdateUserProfileMutation,
  useSignupAgencyOwnerMutation,
  useGetUserInvitationQuery,
  useUploadProfileImageMutation,
  useDeleteProfileImageMutation,
  useCompleteTourMutation,
} = userApi;
